import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminPanel = ({ adminAuth, onClose }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState({});

  const API_BASE = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchPendingRequests();
  }, []);

  const fetchPendingRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/admin-requests`, {
        action: 'admin_get_requests',
        admin_token: adminAuth.token
      });
      setRequests(response.data.pending_requests || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId, newLimit = null) => {
    setProcessing(prev => ({ ...prev, [requestId]: 'approving' }));
    try {
      await axios.post(`${API_BASE}/admin-approve`, {
        action: 'admin_approve',
        request_id: requestId,
        admin_id: adminAuth.user_id,
        new_limit: newLimit
      });
      
      setRequests(prev => prev.filter(req => req.request_id !== requestId));
    } catch (error) {
      console.error('Error approving request:', error);
    } finally {
      setProcessing(prev => ({ ...prev, [requestId]: null }));
    }
  };

  const handleReject = async (requestId) => {
    setProcessing(prev => ({ ...prev, [requestId]: 'rejecting' }));
    try {
      await axios.post(`${API_BASE}/admin-reject`, {
        action: 'admin_reject',
        request_id: requestId,
        admin_id: adminAuth.user_id
      });
      
      setRequests(prev => prev.filter(req => req.request_id !== requestId));
    } catch (error) {
      console.error('Error rejecting request:', error);
    } finally {
      setProcessing(prev => ({ ...prev, [requestId]: null }));
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>🛡️ Admin Panel - User Approval Requests</h2>
        <button onClick={onClose} className="close-btn">×</button>
      </div>

      {loading ? (
        <div className="loading">Loading requests...</div>
      ) : (
        <div className="requests-list">
          {requests.length === 0 ? (
            <div className="no-requests">
              <p>No pending approval requests</p>
            </div>
          ) : (
            requests.map(request => (
              <RequestCard 
                key={request.request_id}
                request={request}
                processing={processing[request.request_id]}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))
          )}
        </div>
      )}

      <style>{`
        .admin-panel {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: white;
          z-index: 10000;
          overflow-y: auto;
        }

        .admin-header {
          background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
          color: white;
          padding: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .admin-header h2 {
          margin: 0;
          font-size: 24px;
        }

        .close-btn {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-size: 24px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .requests-list {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .no-requests {
          text-align: center;
          padding: 60px 20px;
          color: #666;
        }

        .no-requests p {
          font-size: 18px;
          margin: 0;
        }

        @media (max-width: 768px) {
          .admin-header {
            padding: 15px;
          }
          
          .admin-header h2 {
            font-size: 18px;
          }
          
          .requests-list {
            padding: 15px;
          }
        }
      `}</style>
    </div>
  );
};

const RequestCard = ({ request, processing, onApprove, onReject }) => {
  const [newLimit, setNewLimit] = useState(10);

  return (
    <div className="request-card">
      <div className="request-header">
        <div className="user-info">
          <h3>{request.user_name}</h3>
          <p>{request.user_email}</p>
        </div>
        <div className="request-date">
          {new Date(request.requested_at).toLocaleDateString()}
        </div>
      </div>

      <div className="request-details">
        <div className="detail-item">
          <span className="label">User ID:</span>
          <span className="value">{request.user_id}</span>
        </div>
        <div className="detail-item">
          <span className="label">Status:</span>
          <span className="value status">{request.status}</span>
        </div>
      </div>

      <div className="limit-setting">
        <label>New Usage Limit:</label>
        <input 
          type="number" 
          value={newLimit}
          onChange={(e) => setNewLimit(parseInt(e.target.value))}
          min="1"
          max="100"
        />
      </div>

      <div className="request-actions">
        <button 
          onClick={() => onApprove(request.request_id, newLimit)}
          disabled={processing}
          className="approve-btn"
        >
          {processing === 'approving' ? 'Approving...' : `✓ Approve (${newLimit} uses)`}
        </button>
        <button 
          onClick={() => onReject(request.request_id)}
          disabled={processing}
          className="reject-btn"
        >
          {processing === 'rejecting' ? 'Rejecting...' : '✗ Reject'}
        </button>
      </div>

      <style>{`
        .request-card {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .request-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .user-info h3 {
          margin: 0 0 4px 0;
          color: #333;
          font-size: 18px;
        }

        .user-info p {
          margin: 0;
          color: #666;
          font-size: 14px;
        }

        .request-date {
          color: #999;
          font-size: 12px;
        }

        .request-details {
          margin-bottom: 16px;
        }

        .detail-item {
          display: flex;
          margin-bottom: 8px;
        }

        .label {
          font-weight: 600;
          color: #555;
          width: 100px;
        }

        .value {
          color: #333;
        }

        .status {
          background: #fff3cd;
          color: #856404;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          text-transform: uppercase;
        }

        .limit-setting {
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .limit-setting label {
          font-weight: 600;
          color: #555;
        }

        .limit-setting input {
          width: 80px;
          padding: 6px 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .request-actions {
          display: flex;
          gap: 12px;
        }

        .approve-btn, .reject-btn {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .approve-btn {
          background: #4caf50;
          color: white;
        }

        .approve-btn:hover:not(:disabled) {
          background: #45a049;
        }

        .reject-btn {
          background: #f44336;
          color: white;
        }

        .reject-btn:hover:not(:disabled) {
          background: #da190b;
        }

        .approve-btn:disabled, .reject-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .request-card {
            padding: 16px;
          }
          
          .request-header {
            flex-direction: column;
            gap: 8px;
          }
          
          .limit-setting {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
          
          .request-actions {
            flex-direction: column;
          }
          
          .approve-btn, .reject-btn {
            width: 100%;
            padding: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminPanel;